import {
  addMonths,
  addWeeks,
  format as formatDate,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { getScheduleViewDateRange } from './schedulePlannedDateWindow';

/** Matches {@link ScheduleTimelineScale} on the schedule page. */
export type ScheduleGanttScalePreset = 'today' | 'weeks' | 'months';

/**
 * Timeline row configuration for the Gantt `scales` prop (same shapes as used in Task/Resource charts).
 */
function scalesConfigForPreset(preset: ScheduleGanttScalePreset) {
  if (preset === 'today') {
    return [
      { unit: 'month' as const, step: 1, format: '%F %Y' },
      { unit: 'day' as const, step: 1, format: (d: Date): string => formatDate(d, 'EEE dd MMM') },
    ];
  }
  if (preset === 'months') {
    return [
      { unit: 'year' as const, step: 1, format: '%Y' },
      { unit: 'month' as const, step: 1, format: '%F' },
    ];
  }
  return [
    { unit: 'month' as const, step: 1, format: '%F %Y' },
    { unit: 'week' as const, step: 1, format: (start: Date): string => formatDate(start, 'EEE dd MMM') },
  ];
}

/**
 * Visible date span + scale granularity for schedule Gantt charts.
 *
 * - When start/end filters define a window ({@link getScheduleViewDateRange}), **dateFrom/dateTo** follow
 *   those filters; **Today / Weeks / Months** only changes timeline zoom (day vs week vs month ticks).
 * - When no filter dates, rolling windows around “now” match the previous behaviour.
 */
export function computeScheduleGanttTimeline(
  startDate: string,
  endDate: string,
  preset: ScheduleGanttScalePreset
): { dateFrom: string; dateTo: string; scalesConfig: ReturnType<typeof scalesConfigForPreset> } {
  const planned = getScheduleViewDateRange(startDate, endDate);
  if (planned) {
    return {
      dateFrom: formatDate(planned.from, 'yyyy-MM-dd'),
      dateTo: formatDate(planned.to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset(preset),
    };
  }

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });

  if (preset === 'today') {
    const from = subWeeks(weekStart, 1);
    const to = addWeeks(weekStart, 3);
    return {
      dateFrom: formatDate(from, 'yyyy-MM-dd'),
      dateTo: formatDate(to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset('today'),
    };
  }
  if (preset === 'months') {
    const from = subMonths(weekStart, 2);
    const to = addMonths(weekStart, 8);
    return {
      dateFrom: formatDate(from, 'yyyy-MM-dd'),
      dateTo: formatDate(to, 'yyyy-MM-dd'),
      scalesConfig: scalesConfigForPreset('months'),
    };
  }

  const from = subWeeks(weekStart, 4);
  const to = addMonths(weekStart, 4);
  return {
    dateFrom: formatDate(from, 'yyyy-MM-dd'),
    dateTo: formatDate(to, 'yyyy-MM-dd'),
    scalesConfig: scalesConfigForPreset('weeks'),
  };
}
